import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import classNames from 'classnames';
import { createRef, useEffect, useState } from 'react';

import RichText from '@/components/RichText/RichText';
import type {
  CardGroupContent,
  Contentful,
} from '@/contentful/contentful-exports';
import { openLinkCurrentTab } from '@/contentful/utils';
import useDeviceWidth from '@/hooks/deviceWidth';
import { trackEvents } from '@/utils/analytics';
import { setImgWidth } from '@/utils/frontify';

import useStyles from './FAQInfoAccordion.styles';

export interface FAQInfoAccordionContent extends Contentful {
  fields: {
    subtitle: string;
    title: string;
    expandableSections: CardGroupContent[];
    expandedSectionBackgroundColor: 'OffWhite' | 'White';
  };
}

type Props = {
  content: FAQInfoAccordionContent;
  isNested?: boolean;
};

/**
 * @deprecated V2 component
 */
export default function FAQInfoAccordion({ content, isNested }: Props) {
  const { classes, cx } = useStyles();

  const {
    title,
    expandableSections,
    subtitle,
    expandedSectionBackgroundColor,
  } = content.fields;

  const initialFaqstate = Array(expandableSections?.length).fill(false);

  // ToDo consider moving maxCount to contentful
  const maxCount = 5;

  const { isMobile } = useDeviceWidth();
  const [faqState, setFaqState] = useState(initialFaqstate);
  const [viewMore, setViewMore] = useState(false);

  const accordionRefs = expandableSections.map(() =>
    createRef<HTMLDivElement>()
  );

  const formattedTitle = (title: string) => {
    return `faq-${title
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')}`;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    // Get the hash from the URL
    const hash = window.location.hash.substring(1);

    // If the hash starts with 'faq-', extract the index and expand the corresponding FAQ
    if (hash.startsWith('faq-')) {
      const sectionIndex = expandableSections.findIndex((section) => {
        return formattedTitle(section?.fields?.title) === hash;
      });
      if (sectionIndex >= 0) {
        const newState = [...faqState].fill(false);
        newState[sectionIndex] = true;
        setFaqState(newState);
      }
    }
  }, []);

  // useEffect(() => {
  //   if (viewMore) {
  //     window.location.href = `#panel${10}a-header`;
  //   }
  // }, [viewMore]);
  const faqQuestionContainerStyle = !isNested
    ? classes.faqQuestionContainer
    : classes.faqQuestionContainerNoBorder;
  const faqQuestionStyle = !isNested
    ? classes.faqQuestion
    : cx(classes.faqQuestion, classes.faqQuestionNested);
  const accordionDetailsStyle = !isNested
    ? classNames([
        classes.detailsPanel,
        classes[expandedSectionBackgroundColor],
      ])
    : classNames([
        classes.detailsPanelNoPadding,
        classes[expandedSectionBackgroundColor],
      ]);

  return (
    <div
      className={classNames([
        classes.faqWrapper,
        classes[`${expandedSectionBackgroundColor}Expanded`],
      ])}
    >
      {!isNested && subtitle && (
        <p className={classes.titleLabel}>{subtitle}</p>
      )}
      {!isNested && title && <h1 className={classes.title}>{title}</h1>}
      {expandableSections?.length > 0 &&
        expandableSections
          .slice(0, viewMore ? expandableSections.length : maxCount)
          .map((cardGroup, index) => {
            const renderImages = cardGroup.fields.cards.some(
              (card) => card?.fields?.image?.[0]
            );
            return (
              <Accordion
                ref={accordionRefs[index]}
                id={formattedTitle(cardGroup?.fields?.title)}
                classes={{ root: classes.root }}
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                key={index}
                expanded={faqState[index]}
                className={faqQuestionContainerStyle}
                onChange={() => {
                  const currentstate = faqState[index];
                  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
                  let newState;
                  if (isMobile) {
                    newState = [...faqState];
                    newState[index] = !currentstate;
                  } else {
                    newState = [...faqState].fill(false);
                    newState[index] = !currentstate;
                  }
                  const openIndex = newState.indexOf(true);
                  if (openIndex !== -1) {
                    trackEvents([
                      {
                        event: 'FAQ',
                        faq_text: expandableSections[openIndex]?.fields.title,
                        faq_placement: index,
                      },
                    ]);
                  }
                  setFaqState(newState);
                }}
              >
                <AccordionSummary
                  expandIcon={
                    faqState[index]
                      ? !isNested && (
                          <RemoveCircleIcon
                            color='primary'
                            className={classes.icons}
                          />
                        )
                      : !isNested && (
                          <AddCircleIcon
                            color='primary'
                            className={classes.icons}
                          />
                        )
                  }
                  aria-controls={`panel${index}a-content`}
                  id={`panel${index}a-header`}
                  className={faqQuestionContainerStyle}
                >
                  <Typography className={faqQuestionStyle}>
                    {cardGroup?.fields?.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={accordionDetailsStyle}>
                  <div className={`${classes.faqAnswerContainer}`}>
                    {/** biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome */}
                    {cardGroup?.fields?.cards?.map &&
                      cardGroup.fields.cards.map((card) => {
                        const {
                          title: cardTitle,
                          subtitle: cardSubtitle,
                          cta: cardCta,
                        } = card.fields;
                        const linkTarget = openLinkCurrentTab(
                          cardCta?.fields?.url
                        )
                          ? ''
                          : '_blank';
                        const linkRef = openLinkCurrentTab(cardCta?.fields?.url)
                          ? ''
                          : 'noopener noreferrer';
                        return (
                          <div className={classes.answerRow} key={card.sys.id}>
                            {renderImages &&
                              (card?.fields?.image?.[0] ? (
                                // biome-ignore lint/performance/noImgElement: Automated migration to Biome
                                <img
                                  src={setImgWidth(
                                    card.fields.image[0].preview_url,
                                    400
                                  )}
                                  className={classes.answerImage}
                                  alt={card?.fields?.cardImageAltText}
                                />
                              ) : (
                                <div className={classes.answerImage} />
                              ))}
                            <div className={classes.answerTextContainer}>
                              {card?.sys?.contentType?.sys?.id !==
                                'faqinfoAccordion' &&
                                cardTitle && <p>{cardTitle}</p>}
                              {card?.sys?.contentType?.sys?.id !==
                                'faqinfoAccordion' &&
                                cardSubtitle && <p>{cardSubtitle}</p>}
                              {card?.sys?.contentType?.sys?.id ===
                              'faqinfoAccordion' ? (
                                <FAQInfoAccordion
                                  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
                                  content={card as any}
                                  isNested={true}
                                />
                              ) : (
                                <>
                                  <div className={classes.answerText}>
                                    {card.fields.richDescription ? (
                                      <RichText
                                        content={card.fields.richDescription}
                                        title=''
                                      />
                                    ) : (
                                      card.fields.description
                                    )}
                                  </div>
                                  {cardCta && (
                                    // biome-ignore lint/correctness/noRestrictedElements: Automated migration to Biome
                                    <a
                                      href={cardCta.fields.url}
                                      target={linkTarget}
                                      rel={linkRef}
                                    >
                                      {cardCta.fields.text}
                                    </a>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
      {expandableSections?.length > maxCount ? (
        <div className={classes.viewMoreWrapper}>
          {/** biome-ignore lint/a11y/useKeyWithClickEvents: Automated migration to Biome */}
          <p
            className={classes.viewMore}
            onClick={() => {
              setViewMore((viewMore) => !viewMore);
            }}
          >
            {viewMore ? 'View less' : 'View More'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
